import {
    UseMutationResult,
    useMutation,
    useQueryClient,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query';
import { API_ROOT } from '@Helpers/apiConfig';
import { createPacerApi } from '@Helpers/pacerApi';
import { SEARCH_QUERY_KEY, GANTT_SEARCH_QUERY_KEY } from '../baselineTasks';
import {
    BulkUpdateAttributeFiltersResponse,
    bulkUpdateAttributeFiltersResponseSchema,
    BulkUpdateAttributesRequest,
    BulkUpdateAttributesResponse,
    bulkUpdateAttributesResponseSchema,
} from './bulkUpdateAttributes.types';

export const FILTERS_QUERY_KEY = 'BulkUpdateAttributeFilters';

// ---- Load Bulk Update Attribute Filters ----

const loadBulkUpdateAttributeFilters = async (baselineId: string): Promise<BulkUpdateAttributeFiltersResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/bulk-update-attribute-filters`, { json: { baselineId } })
            .json();
        const parsed = bulkUpdateAttributeFiltersResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('loadBulkUpdateAttributeFilters: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }], departments: [], baselineJobTitles: [], communities: [], phases: [], tags: [] };
        }
        return parsed.data;
    } catch (error) {
        console.error('loadBulkUpdateAttributeFilters API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to load bulk update attribute filters: ${error.message}`);
        }
        throw new Error('Failed to load bulk update attribute filters: Unknown error');
    }
};

export const useLoadBulkUpdateAttributeFilters = (
    baselineId: string,
    options?: { enabled?: boolean }
): UseQueryResult<BulkUpdateAttributeFiltersResponse> => {
    return useQuery<BulkUpdateAttributeFiltersResponse>({
        queryKey: [FILTERS_QUERY_KEY, baselineId],
        queryFn: () => loadBulkUpdateAttributeFilters(baselineId),
        enabled: options?.enabled ?? true,
    });
};

// ---- Bulk Update Attributes ----

const bulkUpdateAttributes = async (request: BulkUpdateAttributesRequest): Promise<BulkUpdateAttributesResponse> => {
    try {
        const response = await createPacerApi()
            .post(`${API_ROOT}baselinetask/bulk-update-attrib`, { json: request })
            .json();
        const parsed = bulkUpdateAttributesResponseSchema.safeParse(response);
        if (!parsed.success) {
            console.warn('bulkUpdateAttributes: response did not match schema, using fallback', parsed.error);
            return { isError: true, errors: [{ message: 'The server returned an unexpected response. Please try again.' }] };
        }
        return parsed.data;
    } catch (error) {
        console.error('bulkUpdateAttributes API error:', error);
        if (error instanceof Error) {
            throw new Error(`Failed to update attributes: ${error.message}`);
        }
        throw new Error('Failed to update attributes: Unknown error');
    }
};

export const useBulkUpdateAttributes = (
    callback?: (results: BulkUpdateAttributesResponse) => void
): UseMutationResult<BulkUpdateAttributesResponse, Error, BulkUpdateAttributesRequest> => {
    const queryClient = useQueryClient();
    return useMutation<BulkUpdateAttributesResponse, Error, BulkUpdateAttributesRequest>({
        mutationFn: bulkUpdateAttributes,
        onSuccess: (mutationResponse) => {
            callback?.(mutationResponse);

            if (!mutationResponse.isError) {
                queryClient.invalidateQueries({ queryKey: [SEARCH_QUERY_KEY] });
                queryClient.invalidateQueries({ queryKey: [GANTT_SEARCH_QUERY_KEY] });
            }
        },
    });
};

export * from './bulkUpdateAttributes.types';
